// Inline markdown used by the report text fields.
//
// Bold, italic, underline and strikethrough are supported. Field values are
// stored as plain strings so saved reports stay readable and backwards
// compatible; the markers are the only formatting that ever reaches storage.
//
// Marker characters the user types themselves are escaped on the way in (see
// escape_literals) so they can never be mistaken for markers. Users never type
// markers deliberately - the toolbar writes them - so the exact dialect below
// is an internal detail.

// Wraps every value that comes from a user text field, so the docx
// post-processing knows which runs it is allowed to touch. Private use area -
// these cannot be typed on any keyboard and carry no meaning of their own.
// Built with fromCharCode so no invisible characters end up in this source file.
export const RICH_START = String.fromCharCode(0xe000);
export const RICH_END = String.fromCharCode(0xe001);

// Stand-ins for <w:br/> and <w:tab/> while a run is held as a flat string.
export const BREAK_CHAR = String.fromCharCode(0xe002);
export const TAB_CHAR = String.fromCharCode(0xe003);

// Every mark, in the order they are written when several apply to the same
// text. Closing markers are emitted in the reverse order, so output is always
// properly nested.
export const MARKS = [
  { name: "bold", char: "*", length: 2 },
  { name: "italic", char: "*", length: 1 },
  { name: "underline", char: "_", length: 2 },
  { name: "strike", char: "~", length: 2 },
];

// Longest first per character, so "**" is considered before "*".
const BY_CHAR = MARKS.reduce((map, mark) => {
  (map[mark.char] = map[mark.char] || []).push(mark);
  return map;
}, {});

const MARKER_CHARS = Object.keys(BY_CHAR);
const ESCAPE_PATTERN = new RegExp(`([\\\\${MARKER_CHARS.join("")}])`, "g");
const UNESCAPE_PATTERN = new RegExp(`\\\\([\\\\${MARKER_CHARS.join("")}])`, "g");

function empty_flags() {
  return { bold: false, italic: false, underline: false, strike: false };
}

// Escapes the characters that would otherwise be read as markers. Call this on
// text the user typed, never on markers the toolbar produced.
export function escape_literals(text) {
  if (!text) return "";
  return String(text).replace(ESCAPE_PATTERN, "\\$1");
}

// Reverses escape_literals.
export function unescape_literals(text) {
  if (!text) return "";
  return String(text).replace(UNESCAPE_PATTERN, "$1");
}

// True when a marker at `index` is a real marker and not an escaped literal.
function is_escaped(text, index) {
  let backslashes = 0;
  for (let i = index - 1; i >= 0 && text[i] === "\\"; i--) {
    backslashes += 1;
  }
  return backslashes % 2 === 1;
}

function is_space(char) {
  return (
    char === undefined ||
    char === BREAK_CHAR ||
    char === TAB_CHAR ||
    /\s/.test(char)
  );
}

// A marker only counts when it sits tight against its text, the same rule
// CommonMark uses. This is what keeps "2 * 3 * 4" and "a _ b" plain text,
// including in reports written before formatting existed.
function can_open(text, index, run) {
  return !is_space(text[index + run]);
}

function can_close(text, index) {
  return !is_space(text[index - 1]);
}

// Length of the unbroken run of `char` starting at `index`.
function marker_run_length(text, index, char) {
  let run = 0;
  while (text[index + run] === char) run += 1;
  return run;
}

// True when somewhere later there is a marker run able to close `need`
// delimiters. Without this an unterminated marker would format the rest of the
// field instead of staying plain text.
function has_closing_marker(text, from, char, need) {
  for (let i = from; i < text.length; i++) {
    if (text[i] !== char || is_escaped(text, i)) continue;

    const run = marker_run_length(text, i, char);
    if (run >= need && can_close(text, i)) return true;
    i += run - 1;
  }
  return false;
}

// Splits a stored field value into styled segments:
//   [{ text: "Rectifier ", bold: false, ... },
//    { text: "offline",    bold: true,  ... }]
//
// BREAK_CHAR and TAB_CHAR are passed through untouched so callers that care
// about line breaks can split on them afterwards.
//
// `state` is optional. Pass one to continue formatting that was opened in an
// earlier chunk; it is updated in place so the next chunk can carry on. Word
// splits a single field value over several runs, which is why this has to be
// resumable rather than starting clean every time.
export function parse_inline_markdown(value, state) {
  const text = value == null ? "" : String(value);
  const segments = [];
  const flags = Object.assign(empty_flags(), state && state.flags);
  // Which marks are open, oldest first, so a run of markers closes the
  // innermost one first. Needed where formatting meets formatting, as in
  // "*italic*****bold italic***", where four asterisks run together.
  const open = state && state.open ? state.open.slice() : [];
  let buffer = "";

  const flush = () => {
    if (buffer) segments.push(Object.assign({ text: buffer }, flags));
    buffer = "";
  };

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (char === RICH_START || char === RICH_END) continue;

    if (char === "\\" && (text[i + 1] === "\\" || BY_CHAR[text[i + 1]])) {
      buffer += text[i + 1];
      i += 1;
      continue;
    }

    if (BY_CHAR[char]) {
      const run = marker_run_length(text, i, char);
      let left = run;

      // Close what is already open, innermost first. Four underscores after an
      // open underline mean "close it, then open a new one" rather than one
      // marker of double length.
      while (left > 0 && open.length && can_close(text, i)) {
        const innermost = open[open.length - 1];
        if (innermost.char !== char || left < innermost.length) break;

        flush();
        open.pop();
        flags[innermost.name] = false;
        left -= innermost.length;
      }

      // Whatever is left over may open new marks, provided the run hugs the
      // text that follows and is closed later on.
      while (left > 0 && can_open(text, i, run)) {
        const next = BY_CHAR[char].find(
          (mark) =>
            mark.length <= left &&
            !flags[mark.name] &&
            has_closing_marker(text, i + run, char, mark.length)
        );
        if (!next) break;

        flush();
        flags[next.name] = true;
        open.push(next);
        left -= next.length;
      }

      // Anything we could not account for is simply text.
      buffer += char.repeat(left);
      i += run - 1;
      continue;
    }

    buffer += char;
  }

  flush();

  if (state) {
    state.flags = Object.assign(empty_flags(), flags);
    state.open = open;
  }

  return segments;
}

// Turns styled segments back into a stored field value. Segment text is assumed
// to be raw user text and is escaped here.
export function serialize_inline_markdown(segments) {
  // Neighbours with the same formatting are merged first, so an editor that
  // hands over several nodes for one bold phrase still produces "**one two**"
  // rather than "**one****two**".
  const merged = [];
  for (const segment of segments || []) {
    if (!segment || !segment.text) continue;

    const previous = merged[merged.length - 1];
    const same =
      previous && MARKS.every((mark) => !!previous[mark.name] === !!segment[mark.name]);

    if (same) {
      previous.text += segment.text;
    } else {
      merged.push(Object.assign({}, segment, { text: segment.text }));
    }
  }

  let out = "";
  for (const segment of merged) {
    const applied = MARKS.filter((mark) => segment[mark.name]);

    // Markers have to hug their text to be read back as markers, so leading and
    // trailing spaces are moved outside them.
    const [, before, core, after] = segment.text.match(/^(\s*)([\s\S]*?)(\s*)$/);

    if (!core) {
      out += segment.text;
      continue;
    }

    const opening = applied.map((mark) => mark.char.repeat(mark.length)).join("");
    const closing = applied
      .slice()
      .reverse()
      .map((mark) => mark.char.repeat(mark.length))
      .join("");

    out += `${before}${opening}${escape_literals(core)}${closing}${after}`;
  }

  return out;
}

// Strips every marker and returns what the reader would see. Used where a field
// is shown as plain text, such as the collapsed preview.
export function strip_inline_markdown(value) {
  return parse_inline_markdown(value)
    .map((segment) => segment.text)
    .join("");
}

// Wraps a field value for the docx pipeline. Empty values stay empty so that
// "to_include" checks elsewhere keep working.
export function mark_rich_text(value) {
  if (value == null || value === "") return "";
  return `${RICH_START}${value}${RICH_END}`;
}
